import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TaskCustomerRecord, TaskStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiCard, NoddiInfoBanner, NoddiSeparator } from 'noddi-ui-web';
import { customerRoutes, getCarDisplayName } from 'noddi-util';
import { Navigate, useParams } from 'react-router-dom';

export const UnableToCompleteDetails = () => {
  const { slug } = useParams();

  const {
    data: booking,
    isPending: isBookingPending,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug! }
  });

  const {
    data: taskGroup,
    isLoading: isTaskGroupLoading,
    error: taskGroupError
  } = noddiAsync.useGet({
    type: URLKeys.getTaskGroupByBookingCustomer,
    input: { bookingId: booking?.id as number },
    queryConfig: {
      enabled: !!booking?.id
    }
  });

  if (isBookingPending || isTaskGroupLoading) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingError || taskGroupError) {
    return <ApiErrorPageWithTrans error={[bookingError, taskGroupError].filter(Boolean)} />;
  }

  if (!booking.isFullyUnableToComplete && !booking.isPartiallyUnableToComplete) {
    return <Navigate to={customerRoutes.bookingInfo.getPath({ slug: slug! })} />;
  }

  const title = booking.isFullyUnableToComplete ? t`Booking not completed` : t`Parts of the booking were not completed`;

  const unableToCompleteTasks = taskGroup!.tasks.filter((task) => task.status.value === TaskStatus.UNABLE_TO_COMPLETE);

  return (
    <ContentWrapperCustomerApp title={title}>
      <div className='flex flex-col gap-5'>
        <NoddiInfoBanner
          texts={[
            {
              value: t`Paid services that were not completed will be refunded. You can expect to see the money back in your account within 2-3 business days.`
            }
          ]}
        />
        {booking.bookingItemsCar.map((car) => {
          const carTasks = unableToCompleteTasks.filter((task) =>
            task.bookingItemSalesItemIds.some((id) => car.orderLines?.some((ol) => ol.bookingItemSalesItemId === id))
          );

          if (carTasks.length === 0) {
            return null;
          }

          return (
            <div key={car.id} className='flex flex-col gap-3'>
              <div className='flex items-center gap-2'>
                <NoddiIcon name='Car' size='small' />
                <p className='font-semibold'>{getCarDisplayName({ car, showYear: false })}</p>
              </div>
              <div className='flex flex-col gap-2'>
                {carTasks.map((task: TaskCustomerRecord) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </ContentWrapperCustomerApp>
  );
};

const TaskCard = ({ task }: { task: TaskCustomerRecord }) => (
  <NoddiCard className='flex flex-col gap-1'>
    {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded */}
    <p className='text-sm font-medium'>⚠️ {task.name}</p>
    <NoddiSeparator className='my-2' />
    {task.unableToComplete && (
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-2'>
          <NoddiIcon name='MessageSquare' size='small' />
          <p className='text-sm'>
            {/* eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term */}
            <Trans>Comment from technician</Trans>:
          </p>
        </div>
        {task.unableToComplete.commentsPublic && <p className='pl-6 text-sm'>{task.unableToComplete.commentsPublic}</p>}
      </div>
    )}
  </NoddiCard>
);
